import { useState } from 'react';
import { 
    DndContext, 
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Layers, ChevronDown, ImageIcon, Type } from 'lucide-react';
import { useConfiguratorStore } from '@store/useConfiguratorStore';
import { sortInstancesByLayerOrder } from '@utils/layerUtils';
import { LogoInstance, TextInstance } from '@/types';
import './LayersPanel.css';

// --- Sortable Item Component ---
interface SortableLayerItemProps {
    item: { type: 'logo' | 'text'; data: LogoInstance | TextInstance };
    isActive: boolean;
    onSelect: () => void;
    thumbnailUrl?: string;
}

function SortableLayerItem({ item, isActive, onSelect, thumbnailUrl }: SortableLayerItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: item.data.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };



    return (
        <div 
            ref={setNodeRef} 
            style={style} 
            className={`layer-item ${isActive ? 'active' : ''} ${isDragging ? 'dragging' : ''}`}
            onClick={onSelect}
        >
            <div 
                className="layer-drag-handle"
                {...attributes} 
                {...listeners}
                onClick={e => e.stopPropagation()} // Prevent select on drag start
            >
                <GripVertical size={16} />
            </div>

            <div 
                className="layer-thumbnail" 
                title={item.data.name || (item.type === 'logo' ? 'Logo' : 'Text')}
            >
                {item.type === 'logo' && thumbnailUrl ? (
                    <img src={thumbnailUrl} alt="logo" />
                ) : item.type === 'logo' ? (
                    <ImageIcon size={14} className="text-icon" />
                ) : (
                    <Type size={14} className="text-icon" />
                )}
            </div>
        </div>
    );
}


// --- Main Panel Component ---
interface LayersPanelProps {
    printAreaId: string;
}

export default function LayersPanel({ printAreaId }: LayersPanelProps) {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const { 
        logoInstances, 
        textInstances, 
        logos,
        activeLogoInstanceId,
        activeTextInstanceId,
        selectLayer,
        reorderLayer
    } = useConfiguratorStore();

    // 1. Get items for this print area
    const areaLogos = logoInstances.filter(l => l.printAreaId === printAreaId);
    const areaTexts = textInstances.filter(t => t.printAreaId === printAreaId);

    // 2. Sort DESCENDING for the panel (highest layerOrder visually at the top)
    const sortedItems = sortInstancesByLayerOrder(areaLogos, areaTexts, 'desc');

    // 3. DnD Sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // 5px movement before drag starts (allows clicking)
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // 4. Handle Drag End
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        
        if (over && active.id !== over.id) {
            const oldIndex = sortedItems.findIndex(i => i.data.id === active.id);
            const newIndex = sortedItems.findIndex(i => i.data.id === over.id);
            
            if (oldIndex !== -1 && newIndex !== -1) {
                const targetItem = sortedItems[newIndex];
                const activeItem = sortedItems[oldIndex];
                
                // Reorder via store
                reorderLayer(
                    activeItem.type,
                    activeItem.data.id,
                    targetItem.data.layerOrder
                );
            }
        }
    };

    if (sortedItems.length === 0) {
        return null; // Hide panel if no layers
    }

    return (
        <div className={`layers-panel ${isCollapsed ? 'collapsed' : ''}`} onPointerDown={(e) => e.stopPropagation()}>
            <div 
                className="layers-panel-header"
                onClick={() => setIsCollapsed(!isCollapsed)}
                title={isCollapsed ? "Show Layers" : "Hide Layers"}
            >
                <div className="layers-panel-title">
                    <Layers size={16} />
                </div>
                {!isCollapsed && <ChevronDown size={14} />}
            </div>

            {!isCollapsed && (
                <div className="layers-panel-body">
                    <DndContext 
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext 
                            items={sortedItems.map(item => item.data.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className="layers-list">
                                {sortedItems.map((item) => {
                                    const isActive = (item.type === 'logo' && item.data.id === activeLogoInstanceId) ||
                                                     (item.type === 'text' && item.data.id === activeTextInstanceId);
                                    
                                    // Get thumbnail for logos
                                    let thumbnailUrl;
                                    if (item.type === 'logo') {
                                        const logoData = logos.find(l => l.id === (item.data as LogoInstance).logoId);
                                        thumbnailUrl = logoData?.processedImage || logoData?.originalImage;
                                    }

                                    return (
                                        <SortableLayerItem
                                            key={item.data.id}
                                            item={item}
                                            isActive={isActive}
                                            thumbnailUrl={thumbnailUrl}
                                            onSelect={() => selectLayer(item.type, item.data.id)}
                                        />
                                    );
                                })}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            )}
        </div>
    );
}
